'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import {
  MapPin,
  Calendar,
  Clock,
  Car,
  Package,
  Plane,
  Bus,
  Users,
  ArrowRight,
  Calculator,
  RotateCcw,
  ArrowLeftRight } from
'lucide-react';

const TABS = [
{ id: 'taxi', label: 'Taxi', icon: Car },
{ id: 'packages', label: 'Tour Packages', icon: Package },
{ id: 'airport', label: 'Airport Transfer', icon: Plane },
{ id: 'tempo', label: 'Tempo Traveller', icon: Users },
{ id: 'bus', label: 'Bus', icon: Bus }];


const TRIP_TYPES = ['One Way', 'Round Trip', 'Multi City'];

const POPULAR_ROUTES = [
'Delhi → Shimla',
'Chandigarh → Manali',
'Delhi → Haridwar',
'Amritsar → Dharamshala',
'Delhi → Jaipur',
'Chandigarh → Shimla'];


export default function HeroSection() {
  const [activeTab, setActiveTab] = useState('taxi');
  const [tripType, setTripType] = useState('One Way');
  const [pickup, setPickup] = useState('');
  const [drop, setDrop] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [packageDest, setPackageDest] = useState('');
  const [packageDuration, setPackageDuration] = useState('');
  const [packageBudget, setPackageBudget] = useState('');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <AppImage
          src="https://images.unsplash.com/photo-1717711084068-e46d84806096"
          alt="Scenic mountain landscape with snow-capped peaks and winding road through valley"
          fill
          priority
          className="object-cover"
          sizes="100vw" />
        
        <div className="hero-overlay absolute inset-0" />
      </div>
      {/* Content */}
      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pt-24 pb-16">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="glass-card-dark rounded-full px-4 py-1.5 mb-5 inline-flex items-center gap-2">
            <span className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
            <span className="text-white/90 text-sm font-medium">
              Trusted by 10,000+ Happy Travelers
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight text-balance mb-4">
            Your Journey,{' '}
            <span className="text-accent">Our Passion</span>
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl leading-relaxed">
            Book taxi services, tour packages, and airport transfers across Himachal Pradesh,
            Kashmir, Rajasthan, and Uttarakhand — instantly.
          </p>
        </div>

        {/* Search Card */}
        <div className="glass-card rounded-2xl max-w-4xl mx-auto overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center gap-1 p-3 border-b border-border overflow-x-auto scroll-hide bg-muted/30">
            {TABS?.map((tab) =>
            <button
              key={`hero-tab-${tab?.id}`}
              onClick={() => setActiveTab(tab?.id)}
              className={`pill-tab flex items-center gap-2 flex-shrink-0 text-sm ${
              activeTab === tab?.id ? 'active' : ''}`
              }>
              
                <tab.icon size={15} />
                {tab?.label}
              </button>
            )}
          </div>

          {/* Form Body */}
          <div className="p-5 lg:p-6">
            {activeTab === 'packages' ?
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Destination
                  </label>
                  <div className="relative">
                    <MapPin
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                  
                    <input
                    type="text"
                    className="input-field pl-9"
                    placeholder="e.g. Shimla, Manali, Kashmir"
                    value={packageDest}
                    onChange={(e) => setPackageDest(e?.target?.value)} />
                  
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Duration
                  </label>
                  <select
                  className="input-field"
                  value={packageDuration}
                  onChange={(e) => setPackageDuration(e?.target?.value)}>
                  
                    <option value="">Any Duration</option>
                    <option value="3">3–5 Days</option>
                    <option value="6">6–8 Days</option>
                    <option value="9">9–12 Days</option>
                    <option value="13">13+ Days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Budget (per person)
                  </label>
                  <select
                  className="input-field"
                  value={packageBudget}
                  onChange={(e) => setPackageBudget(e?.target?.value)}>
                  
                    <option value="">Any Budget</option>
                    <option value="10000">Under ₹10,000</option>
                    <option value="20000">₹10,000–₹20,000</option>
                    <option value="30000">₹20,000–₹30,000</option>
                    <option value="50000">₹30,000+</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <Link href="/tour-packages" className="btn-primary w-full justify-center">
                    <Package size={16} />
                    Search Tour Packages
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </div> :

            <>
                {/* Trip Type Toggle */}
                <div className="flex items-center gap-2 mb-4">
                  {TRIP_TYPES?.map((type) =>
                <button
                  key={`trip-type-${type}`}
                  onClick={() => setTripType(type)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all duration-150 ${
                  tripType === type ?
                  'border-primary bg-primary text-white' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`
                  }>
                  
                      {type === 'Round Trip' && <RotateCcw size={13} />}
                      {type === 'Multi City' && <ArrowLeftRight size={13} />}
                      {type}
                    </button>
                )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Pickup */}
                  <div className="lg:col-span-1">
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                      Pickup Location
                    </label>
                    <div className="relative">
                      <MapPin
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary" />
                    
                      <input
                      type="text"
                      className="input-field pl-9"
                      placeholder="City, Airport, Hotel..."
                      value={pickup}
                      onChange={(e) => setPickup(e?.target?.value)}
                      list="pickup-suggestions" />
                    
                      <datalist id="pickup-suggestions">
                        {['Delhi', 'Chandigarh', 'Shimla', 'Amritsar', 'Jaipur', 'Dehradun']?.map(
                        (c) =>
                        <option key={`pickup-opt-${c}`} value={c} />

                      )}
                      </datalist>
                    </div>
                  </div>

                  {/* Drop */}
                  <div className="lg:col-span-1">
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                      Drop Location
                    </label>
                    <div className="relative">
                      <MapPin
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
                    
                      <input
                      type="text"
                      className="input-field pl-9"
                      placeholder="City, Airport, Hotel..."
                      value={drop}
                      onChange={(e) => setDrop(e?.target?.value)}
                      list="drop-suggestions" />
                    
                      <datalist id="drop-suggestions">
                        {['Shimla', 'Manali', 'Dharamshala', 'Haridwar', 'Jaipur', 'Srinagar']?.map(
                        (c) =>
                        <option key={`drop-opt-${c}`} value={c} />

                      )}
                      </datalist>
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                      Pickup Date
                    </label>
                    <div className="relative">
                      <Calendar
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                    
                      <input
                      type="date"
                      className="input-field pl-9"
                      value={date}
                      onChange={(e) => setDate(e?.target?.value)} />
                    
                    </div>
                  </div>

                  {/* Time */}
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                      Pickup Time
                    </label>
                    <div className="relative">
                      <Clock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                    
                      <input
                      type="time"
                      className="input-field pl-9"
                      value={time}
                      onChange={(e) => setTime(e?.target?.value)} />
                    
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Link
                  href="/taxi-booking"
                  className="btn-primary flex-1 justify-center text-base py-3">
                  
                    <Calculator size={17} />
                    Calculate Fare
                  </Link>
                  <Link
                  href="/taxi-booking"
                  className="btn-accent flex-1 justify-center text-base py-3">
                  
                    Book Now
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </>
            }
          </div>

          {/* Popular Routes */}
          <div className="px-5 lg:px-6 pb-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-shrink-0">
              Popular:
            </span>
            {POPULAR_ROUTES?.map((route) =>
            <button
              key={`popular-route-${route}`}
              onClick={() => {
                const parts = route?.split(' → ');
                if (parts?.[0] && parts?.[1]) {
                  setPickup(parts?.[0]);
                  setDrop(parts?.[1]);
                }
              }}
              className="text-xs font-medium text-primary bg-primary-light px-3 py-1 rounded-full hover:bg-primary hover:text-white transition-all duration-150">
              
                {route}
              </button>
            )}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
          {[
          { icon: '✓', text: 'Instant Confirmation' },
          { icon: '✓', text: 'No Hidden Charges' },
          { icon: '✓', text: 'GST Invoice' },
          { icon: '✓', text: 'WhatsApp Updates' },
          { icon: '✓', text: '24/7 Support' }]?.
          map((badge) =>
          <div
            key={`trust-${badge?.text}`}
            className="flex items-center gap-2 glass-card-dark rounded-full px-4 py-2">
            
              <span className="text-secondary font-bold text-sm">{badge?.icon}</span>
              <span className="text-white/90 text-sm font-medium">{badge?.text}</span>
            </div>
          )}
        </div>
      </div>
      {/* Bottom scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <div className="w-5 h-8 border-2 border-white/40 rounded-full flex items-start justify-center pt-1">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>);


}